import React, { useEffect, useState, useRef,useMemo } from "react";

import axios from "axios";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Popover,
  FormControlLabel,
  Switch,
  TextField,
  Modal,
  Box,
  Button,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import styles from "./createConsentEmail.module.scss";
import ImageGalleryV2 from "../../ImageGallery/imageGalleryV3";
import FP17DCModal from "../Modal/Fp17dc/Fp17dc";
import ConsentGalleryPanel from "../TreatmentFlow/ConsentGalleryPanel";

/* --------------------------- Load CKEditor Script --------------------------- */
const loadCKEditorScript = () =>
  new Promise((resolve) => {
    if (window.CKEDITOR) return resolve();
    const s = document.createElement("script");
    s.src = "/ckeditor/ckeditor.js";
    s.onload = () => resolve();
    document.body.appendChild(s);
  });

const getValidOrder = (value, fallback = null) => {
  const parsedOrder = Number(value);
  return Number.isInteger(parsedOrder) && parsedOrder > 0
    ? parsedOrder
    : fallback;
};

/* --------------------------- Left Sidebar --------------------------- */
const LeftSidebar = ({
  availableClauses,
  onSelectTemplate,
  onPreviewTemplate,
  search,
  setSearch,
}) => {
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const filteredClauses = useMemo(() => {
    return availableClauses.filter((tpl) =>
      (tpl.name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [availableClauses, search]);

  const groupedClauses = useMemo(() => {
    return filteredClauses.reduce((acc, tpl) => {
      const type = tpl.type || "Main";
      if (!acc[type]) acc[type] = [];
      acc[type].push(tpl);
      return acc;
    }, {});
  }, [filteredClauses]);

  const renderTemplateList = () => {
    if (Object.entries(groupedClauses).length === 0) {
      return (
        <div className={styles.templatesEmptyWrap}>
          <div className={styles.templatesEmptyIcon}>⌕</div>
          <p className={styles.emptyState}>No matching templates found.</p>
        </div>
      );
    }

    return Object.entries(groupedClauses).map(([type, list]) => (
      <section key={type} className={styles.templateSection}>
        <div className={styles.templateSectionHeader}>
          <h5 className={styles.templateGroupTitle}>{type} Templates</h5>
          <span className={styles.templateGroupCount}>{list.length}</span>
        </div>

        <div className={styles.templateCards}>
          {list.map((tpl) => (
            <div key={tpl.id} className={styles.templateCard}>
              <div className={styles.templateCardTop}>
                <div>
                  <div className={styles.templateTitle}>{tpl.name}</div>
                  {/* <div className={styles.templateMeta}>
                    {tpl.type || "Template"}
                  </div> */}
                </div>
              </div>

              <div className={styles.templateCardActions}>
                <button
                  onClick={() => {
                    onPreviewTemplate?.(tpl);
                  }}
                  className={`${styles.btn} ${styles.btnLight} ${styles.btnTemplateSmall}`}
                  type="button"
                >
                  Preview
                </button>

                <button
                  onClick={() => {
                    onSelectTemplate(tpl);
                    setTemplatesOpen(false);
                  }}
                  className={`${styles.btn} ${styles.btnTemplate} ${styles.btnTemplateSmall}`}
                  type="button"
                >
                  Add Template
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    ));
  };

  return (
    <>
      <aside className={styles.left}>
        <div className={styles.templatesPanel}>
          <div className={styles.templatesHero}>
            <div>
              <p className={styles.templatesEyebrow}>Consent Builder</p>
              <h4 className={styles.sideTitle}>Available Templates</h4>
              <p className={styles.templatesSubText}>
                Quickly insert and preview reusable consent sections.
              </p>
            </div>

           
          </div>

          <div className={styles.templatesSearchWrap}>
            <TextField
              variant="outlined"
              placeholder="Search templates..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  background: "#ffffff",
                },
              }}
            />
          </div>

          <div className={styles.templatesList}>{renderTemplateList()}</div>
        </div>
      </aside>

      <button
        className={styles.floatingAddBtn}
        onClick={() => setTemplatesOpen(true)}
        type="button"
        aria-label="Open templates"
      >
        +
      </button>

      <div
        className={`${styles.templatesDrawerOverlay} ${
          templatesOpen ? styles.open : ""
        }`}
        onClick={() => setTemplatesOpen(false)}
      />

      <div
        className={`${styles.templatesDrawer} ${
          templatesOpen ? styles.open : ""
        }`}
      >
        <div className={styles.templatesDrawerHeader}>
          <div>
            <p className={styles.templatesEyebrow}>Consent Builder</p>
            <h3 className={styles.sideTitle}>Available Templates</h3>
          </div>

          <button
            className={styles.drawerCloseBtn}
            onClick={() => setTemplatesOpen(false)}
            type="button"
            aria-label="Close templates"
          >
            ×
          </button>
        </div>

        <div className={styles.templatesSearchWrap}>
          <TextField
            variant="outlined"
            placeholder="Search templates..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                background: "#ffffff",
              },
            }}
          />
        </div>

        <div className={styles.templatesList}>{renderTemplateList()}</div>
      </div>
    </>
  );
};

/* --------------------------- Block (CKEditor) --------------------------- */
const Block = ({
  block,
  index,
  onChange,
  onAddBelow,
  onDelete,
  onToggle,
  onSaveBlock,
}) => {
  const editorRef = useRef(null);
  const instanceRef = useRef(null);
  const blockRef = useRef(block);
  const onSaveBlockRef = useRef(onSaveBlock);
  const lastSavedContentRef = useRef(block.text || "");
  const lastRequestedContentRef = useRef(block.text || "");

  blockRef.current = block;
  onSaveBlockRef.current = onSaveBlock;

  const textareaId = `cke-${block.id}`;

  useEffect(() => {
    let cancelled = false;

    const saveEditorContent = async () => {
      const inst = instanceRef.current;
      if (!inst) return true;

      const html = inst.getData();
      const currentBlock = blockRef.current;

      onChange(currentBlock.id, html);

      if (
        html === lastSavedContentRef.current ||
        html === lastRequestedContentRef.current
      ) {
        return true;
      }

      lastRequestedContentRef.current = html;

      const saved = await onSaveBlockRef.current(
        { ...currentBlock, text: html, hasChanges: true }
      );

      if (saved) {
        lastSavedContentRef.current = html;
      } else if (lastRequestedContentRef.current === html) {
        // Allow the next outside click to retry a failed autosave.
        lastRequestedContentRef.current = lastSavedContentRef.current;
      }

      return saved;
    };

    const resizeEditorToContent = (inst) => {
      try {
        if (!inst || !inst.container) return;

        const iframe = inst.container.findOne(".cke_wysiwyg_frame");
        const contents = inst.container.findOne(".cke_contents");

        if (!iframe || !contents) return;

        const iframeEl = iframe.$;
        const doc =
          iframeEl.contentDocument || iframeEl.contentWindow?.document;

        if (!doc || !doc.body) return;

        const newHeight = Math.max(doc.body.scrollHeight + 40, 180);

        contents.setStyle("height", `${newHeight}px`);
        iframe.setStyle("height", `${newHeight}px`);
      } catch (e) {
        console.error("resizeEditorToContent error:", e);
      }
    };

    const init = async () => {
      if (!block.open || !editorRef.current) return;

      await loadCKEditorScript();
      if (cancelled) return;

      const CKEDITOR = window.CKEDITOR;
      if (!CKEDITOR) return;

      editorRef.current.id = textareaId;

      if (CKEDITOR.instances?.[textareaId]) {
        try {
          CKEDITOR.instances[textareaId].destroy(true);
        } catch (e) {}
      }

      const config = {
        removePlugins: "resize",
        resize_enabled: false,
      };

      const inst = CKEDITOR.replace(textareaId, config);
      instanceRef.current = inst;

      inst.on("instanceReady", () => {
        try {
          inst.setData(block.text || "", {
            callback: () => {
              lastSavedContentRef.current = block.text || "";
              lastRequestedContentRef.current = block.text || "";
              setTimeout(() => resizeEditorToContent(inst), 50);
            },
          });

          inst.document.on("keyup", () => {
            setTimeout(() => resizeEditorToContent(inst), 0);
          });

          inst.document.on("paste", () => {
            setTimeout(() => resizeEditorToContent(inst), 50);
          });

          inst.document.on("mouseup", () => {
            setTimeout(() => resizeEditorToContent(inst), 0);
          });
        } catch (e) {
          console.error("instanceReady error:", e);
        }
      });

      inst.on("change", () => {
        const html = inst.getData();
        onChange(block.id, html);
        setTimeout(() => resizeEditorToContent(inst), 0);
      });

      inst.on("blur", () => {
        void saveEditorContent();
      });
    };

    init();

    return () => {
      cancelled = true;

      // This also covers in-app navigation or the editor being unmounted.
      // The request is started before CKEditor is destroyed.
      void saveEditorContent();

      const CKEDITOR = window.CKEDITOR;
      if (CKEDITOR?.instances?.[textareaId]) {
        try {
          CKEDITOR.instances[textareaId].destroy(true);
        } catch (e) {}
      }
      instanceRef.current = null;
    };
  }, [block.open, block.id]);




  return (
    <Draggable draggableId={block.id} index={index} isDragDisabled={block.locked}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={[
            styles.block,
            snapshot.isDragging ? styles.dragging : "",
          ].join(" ")}
        >
          <div className={styles.blockHead}>
            <span
              className={styles.dragHandle}
              {...provided.dragHandleProps}
              style={{
                cursor: block.locked ? "not-allowed" : "grab",
                opacity: block.locked ? 0.3 : 1,
              }}
            >
              ⋮⋮
            </span>

            <span className={styles.blockTitle}>{block.title}</span>

            <div className={styles.headActions}>
              {block.hasChanges && (
                <button
                  className={`${styles.iconBtn} ${styles.save}`}
                  title="Save changes"
                  onClick={async () => {
                    const inst = instanceRef.current;
                    const html = inst?.getData() ?? block.text ?? "";
                    const saved = await onSaveBlock(
                      { ...block, text: html, hasChanges: true }
                    );

                    if (saved) {
                      lastSavedContentRef.current = html;
                      lastRequestedContentRef.current = html;
                    }
                  }}
                >
                  💾
                </button>
              )}

              {!block.locked && (
                <button
                  className={`${styles.iconBtn} ${styles.delete}`}
                  onClick={() => onDelete(block.id)}
                >
                  🗑️
                </button>
              )}

              <button
                className={`${styles.iconBtn} ${styles.collapse} ${
                  block.open ? styles.open : ""
                }`}
                onClick={() => onToggle(block.id)}
              >
                ⌄
              </button>
            </div>
          </div>

          {block.open && (
            <div className={styles.blockBody}>
              <textarea
                ref={editorRef}
                id={textareaId}
                defaultValue={block.text}
                placeholder="Type here…"
              />
            </div>
          )}

          {!block.locked && (
            <div className={styles.addBetweenRow}>
              <button
                className={`${styles.iconBtn} ${styles.plus}`}
                onClick={(e) => onAddBelow(index, e)}
                title="Add a clause below"
              >
                ＋
              </button>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

/* ---------------- Treatment Pricing Block ---------------- */
const PricesBlock = ({ consentBundleId, treatments, onPriceUpdated }) => {
  const [localTreatments, setLocalTreatments] = useState(treatments || []);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceInput, setPriceInput] = useState("");

  useEffect(() => {
    setLocalTreatments(treatments || []);
  }, [treatments]);

  if (!localTreatments || localTreatments.length === 0) return null;

  const handleEditClick = (item) => {
    setEditingPriceId(item.id || item.treatment_id);
    setPriceInput(item.price?.toString() || "");
  };

  const handleSavePrice = async (item) => {
    if (!priceInput || isNaN(parseFloat(priceInput))) {
      toast.error("Enter a valid numeric price");
      return;
    }

    try {
      const targetId = item.id ?? item.treatment_id;

      const endpoint = `${process.env.REACT_APP_BASE_URL_V2}consent_bundle/${consentBundleId}/update_treatment_pricing/${targetId}`;

      const { data } = await axios.put(endpoint, {
        new_price: parseFloat(priceInput),
      });

      toast.success("Price updated!");

      const updatedTreatmentId = data.treatment_id;
      const updatedPrice = data.new_price;

      setLocalTreatments((prev) =>
        prev.map((t) => {
          const keys = [t.treatment_id, t.id, t.treatmentId]
            .filter((v) => v != null)
            .map(String);

          const matchKeys = [updatedTreatmentId, targetId]
            .filter((v) => v != null)
            .map(String);

          const isMatch = keys.some((k) => matchKeys.includes(k));
          return isMatch ? { ...t, price: updatedPrice } : t;
        })
      );

      setEditingPriceId(null);
      setPriceInput(String(updatedPrice));

      onPriceUpdated?.({
        treatment_id: updatedTreatmentId,
        new_price: updatedPrice,
        target_id: targetId,
      });
    } catch (err) {
      console.error("Price update failed:", err);
      toast.error("Failed to update price.");
    }
  };

  return (
    <div className={styles.pricesBlock}>
      <div className={styles.pricesHeader}>
        <h4 className={styles.pricesTitle}>Treatment Pricing</h4>
        <span className={styles.pricesTag}>{localTreatments.length} items</span>
      </div>

      <div className={styles.pricesBody}>
        {localTreatments.map((item) => (
          <div key={item.id || item.treatment_id} className={styles.priceCard}>
            <div className={styles.priceLeft}>
              <div className={styles.priceName}>
                {item.nomenclature || item.description}
              </div>
              <div className={styles.priceDesc}>
                {item.description || "No description available"}
              </div>
            </div>

            <div className={styles.priceRight}>
              {editingPriceId === (item.id || item.treatment_id) ? (
                <>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.priceInput}
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleSavePrice(item)
                    }
                  />
                  <button
                    className={styles.priceSaveBtn}
                    onClick={() => handleSavePrice(item)}
                    type="button"
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <span className={styles.priceValue}>
                    £{parseFloat(item.price || 0).toFixed(2)}
                  </span>
                  <button
                    className={styles.priceEditBtn}
                    onClick={() => handleEditClick(item)}
                    type="button"
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
/* ---------------- Treatment Pricing Block ---------------- */

export default function CreateConsentEmail({
  onNext,
  onBack,
  treatmentData,
  setTreatmentData,
}) {
  const [blocks, setBlocks] = useState([]);
  const blocksRef = useRef([]);
  const blockSaveQueuesRef = useRef(new Map());
  const lastQueuedBlockContentRef = useRef(new Map());
  const [availableClauses, setAvailableClauses] = useState([]);
  const [search, setSearch] = useState("");
  const [showGallery, setShowGallery] = useState(false);

  const [applySignature, setApplySignature] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [isLoadingViewPdf, setIsLoadingViewPdf] = useState(false);

  const [showNoPlaceholderModal, setShowNoPlaceholderModal] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const sigPadRef = useRef();

  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);

  const consentBundleId = treatmentData?.id;

  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplateData, setPreviewTemplateData] = useState(null);
  const [showMissingSignatureModal, setShowMissingSignatureModal] =
    useState(false);
  const [showOwnerRequiredModal, setShowOwnerRequiredModal] = useState(false);

  const [checkAccessRights, setCheckAccessRights] = useState({
    is_owner: true,
    can_edit: true,
  });

  const [fp17dcIsChecked, setFp17dcIsChecked] = useState(false);
  const [showAddButton, setShowAddButton] = useState(false);
  const [showFp17dcModal, setShowFp17dcModal] = useState(false);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  const isOwner = !!checkAccessRights?.is_owner;
  const canEdit = !!checkAccessRights?.can_edit;
  const isEditable = isOwner || canEdit;
  const canApplySignature = isOwner;

  const handleOpen = () => setShowFp17dcModal(true);
  const handleClose = () => setShowFp17dcModal(false);

  const scrollAreaRef = useRef(null);
  const [showScrollBottomBtn, setShowScrollBottomBtn] = useState(false);

  const collapseAll = () => {
    setBlocks((prev) => prev.map((b) => ({ ...b, open: false })));
  };

  const expandAll = () => {
    setBlocks((prev) => prev.map((b) => ({ ...b, open: true })));
  };




const resizeAllEditors = () => {
  const CKEDITOR = window.CKEDITOR;
  if (!CKEDITOR?.instances) return;

  Object.values(CKEDITOR.instances).forEach((inst) => {
    try {
      if (!inst?.container) return;

      const iframe = inst.container.findOne(".cke_wysiwyg_frame");
      const contents = inst.container.findOne(".cke_contents");
      if (!iframe || !contents) return;

      const iframeEl = iframe.$;
      const doc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
      if (!doc?.body) return;

      const newHeight = Math.max(doc.body.scrollHeight + 40, 180);

      contents.setStyle("height", `${newHeight}px`);
      iframe.setStyle("height", `${newHeight}px`);
    } catch (e) {
      console.error("resizeAllEditors error:", e);
    }
  });
};


useEffect(() => {
  const t1 = setTimeout(() => resizeAllEditors(), 50);
  const t2 = setTimeout(() => resizeAllEditors(), 180);
  const t3 = setTimeout(() => resizeAllEditors(), 350);

  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
    clearTimeout(t3);
  };
}, [showGallery, blocks.length]);

useEffect(() => {
  const handleResize = () => {
    resizeAllEditors();
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

useEffect(() => {
  const el = scrollAreaRef.current;
  if (!el) return;

  handleScrollAreaScroll();

  el.addEventListener("scroll", handleScrollAreaScroll);
  window.addEventListener("resize", handleScrollAreaScroll);

  return () => {
    el.removeEventListener("scroll", handleScrollAreaScroll);
    window.removeEventListener("resize", handleScrollAreaScroll);
  };
}, [blocks, treatmentData]);

  useEffect(() => {
    const requireFp17dc = !!treatmentData?.require_fp17dc;
    setFp17dcIsChecked(requireFp17dc);
    setShowAddButton(requireFp17dc);
  }, [treatmentData]);

  useEffect(() => {
    const clinicianSig = treatmentData?.consent_form?.clinician_signature;
    const isSigned = clinicianSig && Object.keys(clinicianSig).length > 0;
    setApplySignature(isSigned);
  }, [treatmentData]);

  useEffect(() => {
    const loadClauses = async () => {
      try {
        const { data } = await axios.get(
          `/consent_bundle/${consentBundleId}/get_available_consent_clauses`
        );
        setAvailableClauses(data || []);
      } catch {
        toast.error("Failed to load templates.");
      }
    };
    if (consentBundleId) loadClauses();
  }, [consentBundleId]);

  useEffect(() => {
    if (treatmentData?.consent_form?.clause_blocks?.length) {
      const formatted = treatmentData.consent_form.clause_blocks
        .filter((c) => c.type !== "general")
        .sort((a, b) => a.order - b.order)
        .map((c, idx) => ({
          id: c.template_id?.toString() || `blk-${idx}`,
          title:
            c.type === "intro"
              ? "Introduction"
              : c.type === "outro"
              ? "Conclusion"
              : c.name || "Section",
          text: c.content || "",
          locked: c.type === "intro" || c.type === "outro",
          open: true,
          order: getValidOrder(c.order, idx + 1),
          template_id: c.template_id,
          hasChanges: false,
        }));

      setBlocks((prev) => {
        const localBlocks = new Map(prev.map((b) => [b.id, b]));
        const merged = formatted.map((serverBlock) => {
          const localBlock = localBlocks.get(serverBlock.id);
          return localBlock?.hasChanges
            ? {
                ...serverBlock,
                text: localBlock.text,
                hasChanges: true,
              }
            : serverBlock;
        });

        blocksRef.current = merged;
        return merged;
      });
    }
  }, [treatmentData]);

  useEffect(() => {
    if (!consentBundleId) return;

    const fetchAccessRights = async () => {
      try {
        const { data } = await axios.get(
          `/consent_bundle/${consentBundleId}/check_access_rights`
        );
        setCheckAccessRights(data);
      } catch (error) {
        console.error("Failed to fetch access rights:", error);
        toast.error("Failed to verify access rights. Editing disabled.");
      }
    };

    fetchAccessRights();
  }, [consentBundleId]);

  useEffect(() => {
    const handleOpenGallery = () => {
      setShowGallery(true);
    };

    window.addEventListener("ckeditor-open-gallery", handleOpenGallery);
    return () =>
      window.removeEventListener("ckeditor-open-gallery", handleOpenGallery);
  }, []);

  const handlePreviewTemplate = (tpl) => {
    setPreviewTemplateData(tpl);
    setShowTemplatePreview(true);
  };

  const handleSignatureToggle = async (e) => {
    const checked = e.target.checked;

    if (!checked) {
      setApplySignature(false);
      return;
    }

    setApplySignature(true);

    const hasPlaceholder = blocks.some((b) =>
      (b.text || "").includes("[clinician_signature]")
    );

    if (!hasPlaceholder) {
      setShowNoPlaceholderModal(true);
      setApplySignature(false);
      return;
    }

    const clinicianSig = treatmentData?.consent_form?.clinician_signature;
    const hasSignature = clinicianSig && Object.keys(clinicianSig).length > 0;

    if (!hasSignature) {
      const ok = await handleClinicianSignatureCheck();
      if (!ok) setApplySignature(false);
    }
  };

  const handleSaveSignature = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.warn("Please draw your signature before saving.");
      return;
    }

    const clinicianId = treatmentData?.clinician?.id;
    if (!clinicianId) {
      toast.error("Clinician ID not found.");
      return;
    }

    const token = axios.defaults.headers.common.Authorization;
    const dataUrl = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png");

    if (!consentBundleId) {
      toast.error("Consent bundle ID not found.");
      return;
    }

    const blocksSaved = await saveAllChangedBlocks();
    if (!blocksSaved) return;

    try {
      await axios.post(
        `/practitioner/${clinicianId}/save_signature`,
        { image: dataUrl },
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const markRes = await axios.post(
        `/consent_bundle/${consentBundleId}/mark_as_signed_by_practitioner`,
        null,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      console.log("markRes.data:", markRes?.data);
      if (markRes?.data) {
        console.log("Consent form marked as signed:", markRes.data);
      }

      toast.success("Signature saved and applied successfully!");
      setShowSignaturePad(false);
      setApplySignature(true);
    } catch (err) {
      console.error("Signature save failed:", err);
      toast.error("Failed to save signature.");
    }
  };

  const saveConsentForm = async (updatedBlocks = blocks) => {
    try {
      const payload = {
        clause_blocks: updatedBlocks.map((b, idx) => ({
          order: idx + 1,
          body: b.text,
          template_id: b.template_id,
        })),
      };

      await axios.post(
        `consent_bundle/${consentBundleId}/update_consent_form`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      toast.success("Changes saved!");
      setBlocks((prev) => prev.map((b) => ({ ...b, hasChanges: false })));
      return true;
    } catch (err) {
      console.error("Failed to save consent form:", err);
      toast.error("Failed to save changes");
      return false;
    }
  };

  function cleanTrailingEmptyHtml(html) {
    if (!html) return "";

    const doc = new DOMParser().parseFromString(html, "text/html");
    const root = doc.body;

    const isEmptyNode = (node) => {
      if (!node) return true;
      if (node.nodeType === 1 && node.tagName === "BR") return true;

      const text = (node.textContent || "").replace(/\u00A0/g, "").trim();
      if (text.length > 0) return false;

      if (
        node.querySelector &&
        node.querySelector("img, table, ul, ol, li")
      )
        return false;

      return true;
    };

    while (root.lastChild && isEmptyNode(root.lastChild)) {
      root.removeChild(root.lastChild);
    }

    let changed = true;
    while (changed) {
      changed = false;
      const last = root.lastElementChild;
      if (last && isEmptyNode(last)) {
        last.remove();
        changed = true;
      }
    }

    return root.innerHTML.trim();
  }

  const handleSaveBlock = (
    block,
    order,
    { suppressErrorToast = false } = {}
  ) => {
    const cleanedText = cleanTrailingEmptyHtml(block.text || "");
    const existingSave = blockSaveQueuesRef.current.get(block.id);

    const currentBlockIndex = blocksRef.current.findIndex(
      (currentBlock) => currentBlock.id === block.id
    );
    const currentBlockOrder =
      currentBlockIndex >= 0
        ? getValidOrder(
            blocksRef.current[currentBlockIndex]?.order,
            currentBlockIndex + 1
          )
        : null;
    const resolvedOrder = getValidOrder(
      order,
      getValidOrder(block.order, currentBlockOrder)
    );

    if (!resolvedOrder) {
      console.error("Cannot save clause block: missing a valid order", block);
      if (!suppressErrorToast) {
        toast.error("This section is not ready to save. Please try again.");
      }
      return Promise.resolve(false);
    }

    if (lastQueuedBlockContentRef.current.get(block.id) === cleanedText) {
      return existingSave || Promise.resolve(true);
    }

    lastQueuedBlockContentRef.current.set(block.id, cleanedText);
    const previousSave =
      existingSave || Promise.resolve(true);

    const queuedSave = previousSave
      .catch(() => false)
      .then(async () => {
        try {
          const { data } = await axios.put(
            `/consent_bundle/${consentBundleId}/update_clause_block/${resolvedOrder}`,
            { body: cleanedText },
            { headers: { "Content-Type": "application/json" } }
          );

          if (data) setTreatmentData(data);

          setBlocks((prev) => {
            const next = prev.map((b) =>
              b.id === block.id &&
              cleanTrailingEmptyHtml(b.text || "") === cleanedText
                ? { ...b, hasChanges: false }
                : b
            );
            blocksRef.current = next;
            return next;
          });

          return true;
        } catch (err) {
          console.error("Failed to save block:", err);
          if (
            lastQueuedBlockContentRef.current.get(block.id) === cleanedText
          ) {
            lastQueuedBlockContentRef.current.delete(block.id);
          }
          if (!suppressErrorToast) toast.error("Failed to save block");
          return false;
        }
      });

    blockSaveQueuesRef.current.set(block.id, queuedSave);
    void queuedSave.finally(() => {
      if (blockSaveQueuesRef.current.get(block.id) === queuedSave) {
        blockSaveQueuesRef.current.delete(block.id);
      }
    });

    return queuedSave;
  };

  const previewPdf = async () => {
    setIsLoadingViewPdf(true);
    try {
      const consentForm = blocks.map((b) => b.text || "").join("<hr/>");
      const res = await axios({
        url: `/consent_bundle/${consentBundleId}/generate_preview_pdf`,
        method: "POST",
        responseType: "blob",
        data: { consentForm },
      });
      const href = URL.createObjectURL(res.data);
      window.open(href, "_blank");
    } catch {
      toast.error("Failed to generate PDF preview.");
    } finally {
      setIsLoadingViewPdf(false);
    }
  };

  const rebuildBlocksFromConsentForm = (consentForm) => {
    if (!consentForm?.clause_blocks?.length) return [];

    return consentForm.clause_blocks
      .filter((c) => c.type !== "general")
      .sort((a, b) => a.order - b.order)
      .map((c, idx) => ({
        id: c.template_id?.toString() || `blk-${idx}`,
        title:
          c.type === "intro"
            ? "Introduction"
            : c.type === "outro"
            ? "Conclusion"
            : c.name || "Section",
        text: c.content || "",
        locked: c.type === "intro" || c.type === "outro",
        open: true,
        order: getValidOrder(c.order, idx + 1),
        template_id: c.template_id,
        hasChanges: false,
      }));
  };

  const refreshPricing = async () => {
    if (isUpdatingPrice) return;
    const savedOk = await saveAllChangedBlocks();
    if (!savedOk) return;

    setIsUpdatingPrice(true);

    try {
      const { data } = await axios.patch(
        `/consent_bundle/${consentBundleId}/refresh_treatment_pricing`
      );
      toast.success("Treatment Pricing Updated");

      if (!data) return;
      setTreatmentData(data);

      if (data?.consent_form) {
        const rebuiltBlocks = rebuildBlocksFromConsentForm(data.consent_form);
        setBlocks(rebuiltBlocks);
      }
    } catch (err) {
      console.error("Failed to update treatment pricing", err);
      toast.error("Failed to update treatment pricing");
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  const moveClause = async (oldPos, newPos) => {
    try {
      await axios.put(
        `/consent_bundle/${consentBundleId}/move_position_of_clause_block/${oldPos}`,
        { new_position: newPos },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
    } catch (err) {
      console.error("Failed to move clause:", err);
      toast.error("Failed to reorder clauses.");
    }
  };

  const onDragUpdate = async (update) => {
    if (!update.destination) return;
    const sourceIndex = update.source.index;
    const destIndex = update.destination.index;
    if (sourceIndex === destIndex) return;

    await moveClause(sourceIndex + 1, destIndex + 1);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const next = [...blocks];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(destIndex, 0, moved);

    const withOrders = next.map((c, idx) => ({ ...c, order: idx + 1 }));
    setBlocks(withOrders);

    await moveClause(moved.order, destIndex + 1);
  };

  const groupedClauses = availableClauses.reduce((acc, tpl) => {
    const type = tpl.type || "Main";
    if (!acc[type]) acc[type] = [];
    acc[type].push(tpl);
    return acc;
  }, {});

  const onAddBelow = (index, e) => {
    e.stopPropagation();
    setInsertIndex(index);
    setPickerAnchor(e.currentTarget);
  };

  const saveSelectedConsentClauses = async (clauseIds) => {
    if (!consentBundleId) {
      console.warn("Missing consentBundleId");
      return;
    }

    try {
      const combinedClauseIds = clauseIds.map((id) => String(id));

      const { data } = await axios.put(
        `/consent_bundle/${consentBundleId}/update_consent_form_with_selected_templates`,
        { block_ids: combinedClauseIds },
        { headers: { "Content-Type": "application/json" } }
      );

      if (data?.consent_form?.clause_blocks?.length) {
        const formattedBlocks = data.consent_form.clause_blocks
          .filter((c) => c.type !== "general")
          .sort((a, b) => a.order - b.order)
          .map((c, idx) => ({
            id: c.template_id?.toString() || `blk-${idx}`,
            title:
              c.type === "intro"
                ? "Introduction"
                : c.type === "outro"
                ? "Conclusion"
                : c.name || "Section",
            text: c.content || "",
            locked: c.type === "intro" || c.type === "outro",
            open: true,
            order: getValidOrder(c.order, idx + 1),
            template_id: c.template_id,
          }));

        setBlocks(formattedBlocks);
        setTreatmentData(data);
      } else if (data) {
        setTreatmentData(data);
      }

      toast.success("Consent clauses synced and reloaded!");
    } catch (error) {
      console.error("Error updating selected consent clauses:", error);
      toast.error("Failed to sync selected consent clauses.");
    }
  };

  const handlePickTemplate = async (tpl) => {
    if (!tpl && insertIndex == null) return;

    const introIndex = blocks.findIndex(
      (b) => b.locked && b.title === "Introduction"
    );
    const outroIndex = blocks.findIndex(
      (b) => b.locked && b.title === "Conclusion"
    );
    const insertPos = insertIndex + 1;

    if (insertPos <= introIndex) {
      toast.error("Cannot insert before Introduction");
      return;
    }
    if (outroIndex !== -1 && insertPos > outroIndex) {
      toast.error("Cannot insert after Conclusion");
      return;
    }

    const newBlock = {
      id: `tpl-${tpl.id}-${Date.now()}`,
      title: tpl.name || tpl.type || "New Section",
      text: tpl.body || "",
      open: true,
      locked: false,
      order: insertPos + 1,
      template_id: tpl.id,
    };

    const newBlocks = [...blocks];
    newBlocks.splice(insertPos, 0, newBlock);
    const orderedBlocks = newBlocks.map((block, idx) => ({
      ...block,
      order: idx + 1,
    }));
    blocksRef.current = orderedBlocks;
    setBlocks(orderedBlocks);
    setPickerAnchor(null);
    setInsertIndex(null);
    toast.success("Clause added successfully!");

    try {
      const selectedClauseIds = orderedBlocks
        .filter((b) => !b.locked && b.template_id)
        .map((b) => String(b.template_id));

      await saveSelectedConsentClauses(selectedClauseIds);
    } catch (error) {
      console.error("Error syncing clauses:", error);
      toast.error("Failed to sync selected clauses.");
    }
  };

  const saveAllChangedBlocks = async () => {
    const changed = blocksRef.current.filter((b) => b.hasChanges);
    if (changed.length === 0) return true;

    try {
      for (const b of changed) {
        const currentIndex = blocksRef.current.findIndex(
          (currentBlock) => currentBlock.id === b.id
        );
        const saved = await handleSaveBlock(
          b,
          getValidOrder(
            b.order,
            currentIndex >= 0 ? currentIndex + 1 : null
          ),
          {
            suppressErrorToast: true,
          }
        );
        if (!saved) {
          toast.error("Failed to save some changes");
          return false;
        }
      }
      return true;
    } catch (err) {
      toast.error("Failed to save some changes");
      console.error(err);
      return false;
    }
  };

  const saveAllChangedBlocksRef = useRef(saveAllChangedBlocks);
  saveAllChangedBlocksRef.current = saveAllChangedBlocks;

  useEffect(() => {
    return () => {
      // React Router navigation unmounts this component. Start a final silent
      // flush so a changed block is not left behind if it never emitted blur.
      void saveAllChangedBlocksRef.current();
    };
  }, []);

  const handleDeleteBlock = async (blockId) => {
    const blockToDelete = blocks.find((b) => b.id === blockId);
    if (!blockToDelete) return;

    const orderNumber = blockToDelete.order;

    const nextSelected = blocks
      .filter((b) => b.id !== blockId && b.template_id)
      .map((b) => String(b.template_id));

    try {
      const { data } = await axios.delete(
        `/consent_bundle/${consentBundleId}/delete_clause_block/${orderNumber}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          data: {
            block_ids: nextSelected,
          },
        }
      );

      toast.success("Clause deleted successfully");

      if (data?.consent_form?.clause_blocks) {
        const formatted = data.consent_form.clause_blocks
          .sort((a, b) => a.order - b.order)
          .map((c, idx) => ({
            id: c.template_id?.toString() || `blk-${idx}`,
            title:
              c.type === "intro"
                ? "Introduction"
                : c.type === "outro"
                ? "Conclusion"
                : c.name || "Section",
            text: c.content || "",
            locked: c.type === "intro" || c.type === "outro",
            open: true,
            order: getValidOrder(c.order, idx + 1),
            template_id: c.template_id,
          }));

        setBlocks(formatted);
        setTreatmentData(data);
      } else {
        const updatedBlocks = blocks
          .filter((b) => b.id !== blockId)
          .map((b, idx) => ({ ...b, order: idx + 1 }));

        setBlocks(updatedBlocks);
      }
    } catch (err) {
      console.error("Delete clause failed:", err);
      toast.error("Failed to delete clause");
    }
  };

  const handleCheckboxChangetoggle = async (event) => {
    const isChecked = event.target.checked;
    setFp17dcIsChecked(isChecked);

    try {
      const response = await axios.put(
        `/consent_bundle/${consentBundleId}/toggle_fp17dc_form_generation`,
        { enabled: isChecked },
        { headers: { "Content-Type": "application/json" } }
      );

      setShowAddButton(!!response.data);
    } catch (error) {
      console.error("Error updating FP17DC form generation:", error);
      toast.error("Failed to update FP17DC toggle");
    }
  };

  const handleClinicianSignatureCheck = async () => {
    const clinicianId = treatmentData?.clinician?.id;
    if (!clinicianId) {
      toast.error("Clinician data missing.");
      return false;
    }

    try {
      const { data: signatureExists } = await axios.get(
        `/practitioner/${clinicianId}/check_if_signature_exists`
      );

      if (!signatureExists) {
        setShowSignaturePad(true);
        return false;
      }

      const response = await axios.post(
        `/consent_bundle/${consentBundleId}/mark_as_signed_by_practitioner`,
        null,
        {
          headers: {
            Authorization: axios.defaults.headers.common.Authorization,
          },
        }
      );

      if (response?.data) {
        console.log("Consent form marked as signed:", response.data);
      }
      setApplySignature(true);

      toast.success("Clinician signature applied successfully!");
      return true;
    } catch (error) {
      console.error("Error checking clinician signature:", error);
      toast.error("Failed to verify clinician signature.");
      setApplySignature(false);
      return false;
    }
  };

  const allCollapsed = blocks.length > 0 && blocks.every((b) => !b.open);

  const toggleCollapseAll = () => {
    setBlocks((prev) => prev.map((b) => ({ ...b, open: allCollapsed })));
  };


const handleScrollAreaScroll = () => {
  const el = scrollAreaRef.current;
  if (!el) return;

  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  setShowScrollBottomBtn(distanceFromBottom > 120);
};

const scrollToBottom = () => {
  const el = scrollAreaRef.current;
  if (!el) return;

  el.scrollTo({
    top: el.scrollHeight,
    behavior: "smooth",
  });
};

const centerRef = useRef(null);

useEffect(() => {
  if (!centerRef.current || !window.ResizeObserver) return;

  const observer = new ResizeObserver(() => {
    resizeAllEditors();
  });

  observer.observe(centerRef.current);

  return () => observer.disconnect();
}, []);

  return (
    <div
      className={`${styles.root} ${
        showGallery ? styles.withGallery : styles.noGallery
      }`}
    >
      <LeftSidebar
        availableClauses={availableClauses}
        onSelectTemplate={(tpl) => handlePickTemplate(tpl)}
        onPreviewTemplate={handlePreviewTemplate}
        search={search}
        setSearch={setSearch}
      />

      <main ref={centerRef} className={styles.center}>
        <header className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.headerTitleWrap}>
              <h2>Email Consent</h2>
              <p className={styles.headerSub}>
                Edit, reorder, preview, and manage your consent form blocks.
              </p>
            </div>

            <div className={styles.headerActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnLight}`}
                onClick={toggleCollapseAll}
                disabled={blocks.length === 0}
                title={allCollapsed ? "Expand all sections" : "Collapse all sections"}
              >
                {allCollapsed ? "Expand all" : "Collapse all"}
              </button>
            </div>
          </div>
        </header>

        <div className={styles.scrollArea}>
          <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={styles.blocks}
                >
                  {blocks.map((b, i) => (
                    <Block
                      key={b.id}
                      block={b}
                      index={i}
                      onChange={(id, text) =>
                        setBlocks((prev) => {
                          const next = prev.map((blk) =>
                            blk.id === id
                              ? { ...blk, text, hasChanges: true }
                              : blk
                          );
                          blocksRef.current = next;
                          return next;
                        })
                      }
                      onAddBelow={onAddBelow}
                      onDelete={(id) => handleDeleteBlock(id)}
                      onToggle={(id) =>
                        setBlocks((prev) =>
                          prev.map((blk) =>
                            blk.id === id ? { ...blk, open: !blk.open } : blk
                          )
                        )
                      }
                      onSaveBlock={(blk, options) =>
                        handleSaveBlock(
                          blk,
                          getValidOrder(blk.order, i + 1),
                          options
                        )
                      }
                    />
                  ))}

                  <PricesBlock
                    key={treatmentData?.treatment_plan?.treatments?.length}
                    consentBundleId={consentBundleId}
                    treatments={treatmentData?.treatment_plan?.treatments || []}
                    onPriceUpdated={(updated) => {
                      setTreatmentData((prev) => {
                        const list = prev?.treatment_plan?.treatments || [];
                        const matchKeys = [updated.treatment_id, updated.target_id]
                          .filter((v) => v != null)
                          .map(String);

                        const nextTreatments = list.map((t) => {
                          const keys = [t.treatment_id, t.id, t.treatmentId]
                            .filter((v) => v != null)
                            .map(String);

                          const isMatch = keys.some((k) => matchKeys.includes(k));
                          return isMatch ? { ...t, price: updated.new_price } : t;
                        });

                        return {
                          ...prev,
                          treatment_plan: {
                            ...prev.treatment_plan,
                            treatments: nextTreatments,
                          },
                        };
                      });
                    }}
                  />

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <Popover
          open={Boolean(pickerAnchor)}
          anchorEl={pickerAnchor}
          onClose={() => {
            setPickerAnchor(null);
            setInsertIndex(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          PaperProps={{
            sx: {
              width: 340,
              borderRadius: "16px",
              p: 1,
              maxHeight: 420,
              overflowY: "auto",
              boxShadow: "0 20px 45px rgba(15, 23, 42, 0.16)",
              border: "1px solid #e5e7eb",
            },
          }}
        >
          {Object.keys(groupedClauses).length === 0 ? (
            <div style={{ padding: "12px 14px", color: "#777" }}>
              No templates available.
            </div>
          ) : (
            Object.entries(groupedClauses).map(([type, list]) => (
              <div key={type} style={{ marginBottom: "10px" }}>
                <h4
                  style={{
                    margin: "8px 10px",
                    fontSize: "13px",
                    color: "#6b7280",
                    fontWeight: 700,
                  }}
                >
                  {type}
                </h4>

                {list.map((tpl) => (
                  <div
                    key={tpl.id}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "12px",
                      cursor: "pointer",
                      border: "1px solid #edf0f3",
                      margin: "6px 8px",
                      background: "#fafafa",
                      fontSize: "13.5px",
                      fontWeight: 600,
                    }}
                    onClick={() => handlePickTemplate(tpl)}
                    title="Insert this clause here"
                  >
                    {tpl.name}
                  </div>
                ))}
              </div>
            ))
          )}
        </Popover>

        <div className={styles.stickyFooter}>
          <div className={styles.footerLeft}>
            <div className={styles.footerControl}>
              <FormControlLabel
                control={
                  <Switch
                    checked={applySignature}
                    onChange={handleSignatureToggle}
                    color="warning"
                    disabled={!canApplySignature}
                  />
                }
                label={
                  <span className={styles.footerLabelText}>
                    Apply my signature
                  </span>
                }
              />
            </div>

            <div className={styles.footerControl}>
              <FormControlLabel
                label="Enable FP17DC"
                control={
                  <input
                    type="checkbox"
                    checked={fp17dcIsChecked}
                    onChange={handleCheckboxChangetoggle}
                    style={{ width: 16, height: 16 }}
                  />
                }
                sx={{
                  mr: 0,
                  ml: 1,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 13,
                    color: "#333",
                    fontWeight: 600,
                  },
                }}
              />
            </div>

            {fp17dcIsChecked && showAddButton && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnLight}`}
                onClick={handleOpen}
              >
                Edit FP17DC
              </button>
            )}
          </div>

          <div className={styles.footerRight}>
            <button
              className={`${styles.btn} ${styles.btnLight}`}
              disabled={isLoadingViewPdf}
              onClick={async () => {
                const allSaved = await saveAllChangedBlocks();
                if (allSaved) previewPdf();
              }}
              type="button"
            >
              {isLoadingViewPdf ? "Generating…" : "Preview"}
            </button>

            <button
              className={`${styles.btn} ${styles.btnAccent}`}
              onClick={refreshPricing}
              disabled={isUpdatingPrice}
              type="button"
            >
              {isUpdatingPrice ? "Refreshing..." : "Refresh Treatment Pricing"}
            </button>

            <button
              className={`${styles.btn} ${styles.btnLight}`}
              onClick={async () => {
                const allSaved = await saveAllChangedBlocks();
                if (!allSaved) return;
                onBack();
              }}
              type="button"
            >
              Back
            </button>

            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={async () => {
                const allSaved = await saveAllChangedBlocks();
                if (!allSaved) return;

                const hasPlaceholder = blocks.some((b) =>
                  (b.text || "").includes("[clinician_signature]")
                );

                if (hasPlaceholder && !applySignature) {
                  if (!isEditable) {
                    setShowMissingSignatureModal(true);
                    return;
                  }
                  if (!isOwner && canEdit) {
                    setShowOwnerRequiredModal(true);
                    return;
                  }
                  setShowMissingSignatureModal(true);
                  return;
                }

                onNext();
              }}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </main>

      <Modal
        open={showMissingSignatureModal}
        onClose={() => setShowMissingSignatureModal(false)}
      >
        <Box
          sx={{
            width: { xs: "92%", sm: 460 },
            mx: "auto",
            mt: "18vh",
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
              borderBottom: "1px solid #fde68a",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#ffedd5",
                display: "grid",
                placeItems: "center",
                fontSize: 20,
              }}
            >
              ✍️
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Signature required to continue
              </Box>
              <Box sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.3 }}>
                A clinician signature placeholder was detected, but signature
                application is not enabled.
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 3, py: 2.5, textAlign: "left" }}>
            <Box sx={{ fontSize: 14, color: "#374151", mb: 1.2 }}>
              Your consent text includes the placeholder:
            </Box>

            <Box
              sx={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 13.5,
                bgcolor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                px: 2,
                py: 1.2,
                color: "#111827",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <span>[clinician_signature]</span>

              <Button
                size="small"
                variant="text"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText("[clinician_signature]");
                    toast.success("Placeholder copied!");
                  } catch (e) {
                    toast.error("Copy failed. Please copy manually.");
                  }
                }}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 1.2,
                }}
              >
                Copy
              </Button>
            </Box>

            <Box
              sx={{
                mt: 1.6,
                fontSize: 13,
                color: "#6b7280",
                lineHeight: 1.55,
                bgcolor: "#fffbeb",
                border: "1px dashed #f59e0b",
                borderRadius: 2,
                p: 1.6,
              }}
            >
              <b style={{ color: "#92400e" }}>Choose one:</b>
              <ul style={{ margin: "10px 0 0", paddingLeft: "18px" }}>
                <li>
                  Turn on <b>“Apply my signature”</b> to apply the clinician
                  signature automatically.
                </li>
                <li>
                  Or remove <b>[clinician_signature]</b> from the consent text if
                  it’s not needed.
                </li>
              </ul>
            </Box>
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: "1px solid #f3f4f6",
              bgcolor: "#fff",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowMissingSignatureModal(false)}
              sx={{
                bgcolor: "#f59e0b",
                color: "#111827",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                "&:hover": { bgcolor: "#d97706" },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showOwnerRequiredModal}
        onClose={() => setShowOwnerRequiredModal(false)}
      >
        <Box
          sx={{
            width: { xs: "92%", sm: 460 },
            mx: "auto",
            mt: "18vh",
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
              borderBottom: "1px solid #fde68a",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#ffedd5",
                display: "grid",
                placeItems: "center",
                fontSize: 20,
              }}
            >
              ⚠️
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Signature placeholder detected
              </Box>
              <Box sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.3 }}>
                You can proceed, but only an owner can apply a clinician signature.
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 3, py: 2.5, textAlign: "left" }}>
            <Box sx={{ fontSize: 14, color: "#374151", mb: 1.2 }}>
              The consent includes:
            </Box>

            <Box
              sx={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 13.5,
                bgcolor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                px: 2,
                py: 1.2,
                color: "#111827",
              }}
            >
              [clinician_signature]
            </Box>

            <Box
              sx={{
                mt: 1.4,
                fontSize: 13,
                color: "#6b7280",
                lineHeight: 1.55,
                bgcolor: "#fffbeb",
                border: "1px dashed #f59e0b",
                borderRadius: 2,
                p: 1.6,
              }}
            >
              Since you are not the owner, <b>“Apply my signature”</b> is disabled.
              <br />
              You can either:
              <ul style={{ margin: "10px 0 0", paddingLeft: "18px" }}>
                <li>
                  Ask an owner to remove the placeholder if it was added by
                  mistake, or
                </li>
                <li>Proceed without applying a signature.</li>
              </ul>
            </Box>
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: "1px solid #f3f4f6",
              bgcolor: "#fff",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setShowOwnerRequiredModal(false)}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Go back
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setShowOwnerRequiredModal(false);
                onNext();
              }}
              sx={{
                bgcolor: "#f59e0b",
                color: "#111827",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                "&:hover": { bgcolor: "#d97706" },
              }}
            >
              Proceed
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showNoPlaceholderModal}
        onClose={() => setShowNoPlaceholderModal(false)}
      >
        <Box
          sx={{
            width: { xs: "92%", sm: 460 },
            mx: "auto",
            mt: "18vh",
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
              borderBottom: "1px solid #fde68a",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#ffedd5",
                display: "grid",
                placeItems: "center",
                fontSize: 20,
              }}
            >
              ⚠️
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Signature placeholder missing
              </Box>
              <Box sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.3 }}>
                We can’t apply the clinician signature until the placeholder exists
                in the consent text.
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 3, py: 2.5, textAlign: "left" }}>
            {isEditable ? (
              <>
                <Box sx={{ fontSize: 14, color: "#374151", mb: 1.5 }}>
                  Add this placeholder anywhere in your consent body where you want
                  the clinician signature to appear:
                </Box>

                <Box
                  sx={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 13.5,
                    bgcolor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    px: 2,
                    py: 1.2,
                    color: "#111827",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <span>[clinician_signature]</span>

                  <Button
                    size="small"
                    variant="text"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText("[clinician_signature]");
                        toast.success("Placeholder copied!");
                      } catch (e) {
                        toast.error("Copy failed. Please copy manually.");
                      }
                    }}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                      px: 1.2,
                    }}
                  >
                    Copy
                  </Button>
                </Box>

                <Box
                  sx={{
                    mt: 1.6,
                    fontSize: 13,
                    color: "#6b7280",
                    lineHeight: 1.5,
                    bgcolor: "#fffbeb",
                    border: "1px dashed #f59e0b",
                    borderRadius: 2,
                    p: 1.6,
                  }}
                >
                  <b style={{ color: "#92400e" }}>Tip:</b> Place it near the end of
                  the consent form under the clinician name / signature area.
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ fontSize: 14, color: "#374151", mb: 1.2 }}>
                  This consent contains a clinician signature placeholder, but you
                  don’t have edit access to add or adjust it.
                </Box>

                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: 13,
                    color: "#6b7280",
                    lineHeight: 1.55,
                    bgcolor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    p: 1.6,
                  }}
                >
                  To continue, please <b>turn off “Apply my signature”</b> (or ask
                  an owner/editor to update the consent). If the placeholder was
                  added by mistake, an owner/editor can remove{" "}
                  <b>[clinician_signature]</b> from the consent text.
                </Box>

                <Box
                  sx={{
                    mt: 1.2,
                    fontSize: 13,
                    color: "#92400e",
                    bgcolor: "#fffbeb",
                    border: "1px dashed #f59e0b",
                    borderRadius: 2,
                    p: 1.4,
                  }}
                >
                  <b>Note:</b> Since you don’t have access to edit the consent, the
                  safest option is to remove the placeholder by an editor if it’s
                  not intended to be used.
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: "1px solid #f3f4f6",
              bgcolor: "#fff",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowNoPlaceholderModal(false)}
              sx={{
                bgcolor: "#f59e0b",
                color: "#111827",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                "&:hover": { bgcolor: "#d97706" },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showSignaturePad}
        onClose={() => {
          setShowSignaturePad(false);
          setApplySignature(false);
        }}
      >
        <Box
          sx={{
            background: "#fff",
            padding: 3,
            borderRadius: 3,
            width: 520,
            maxWidth: "92vw",
            mx: "auto",
            mt: "10vh",
            textAlign: "center",
            boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>Draw Your Signature</h3>
          <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 14 }}>
            Save once, then it can be applied automatically to the consent form.
          </p>

          <SignatureCanvas
            ref={sigPadRef}
            penColor="black"
            canvasProps={{
              width: 450,
              height: 200,
              className: "sigCanvas",
              style: {
                border: "1px solid #ddd",
                borderRadius: "12px",
                marginTop: "10px",
                background: "#fff",
              },
            }}
          />

          <div
            style={{
              marginTop: "14px",
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <Button variant="outlined" onClick={() => sigPadRef.current.clear()}>
              Clear
            </Button>
            <Button variant="contained" onClick={handleSaveSignature}>
              Save
            </Button>
          </div>
        </Box>
      </Modal>

      {showGallery && (
  <aside className={styles.right}>
    <div className={styles.galleryPane}>
      <ConsentGalleryPanel
        showGallery={showGallery}
        onClose={() => setShowGallery(false)}
      />
    </div>
  </aside>
)}

      <Modal
        open={showTemplatePreview}
        onClose={() => setShowTemplatePreview(false)}
      >
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            width: "70%",
            maxWidth: 860,
            mx: "auto",
            my: "8vh",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            padding: 3,
          }}
        >
          <h2
            style={{
              marginBottom: "10px",
              fontWeight: "700",
              color: "#333",
              marginTop: 0,
            }}
          >
            {previewTemplateData?.name || "Template Preview"}
          </h2>

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "16px",
              maxHeight: "65vh",
              overflowY: "auto",
              fontSize: "13.5px",
              lineHeight: 1.7,
              background: "#fcfcfd",
            }}
            dangerouslySetInnerHTML={{
              __html: previewTemplateData?.body || "<em>No content available</em>",
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setShowTemplatePreview(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <FP17DCModal
        open={showFp17dcModal}
        onClose={handleClose}
        consentBundleId={consentBundleId}
        treatmentData={treatmentData}
        setTreatmentData={setTreatmentData}
      />
    </div>
  );
}
